import { z } from "zod";
import { uuid, paged, isoDate, moneyPaise } from "../_common.js";

/**
 * `bills.extract` — synchronously read a previously-uploaded vendor bill
 * PDF/image through the vision model and return typed fields the SPA
 * uses to pre-fill the composer.
 *
 * Sync (~8-15s) instead of async because the operator is in the "I'm
 * doing this task right now" workflow — they upload the PDF in the
 * composer dropzone and stare at a spinner until fields populate. No
 * polling, no DB row, no status state machine. Fields land inline and
 * the operator reviews/edits before saving via the existing `bills.create`
 * (which stamps `aiExtraction.*` on the row for audit).
 */
export const extractInput = z.object({
  /**
   * Logical (prefix-less) S3 object key of the uploaded source file.
   * Must live under the caller's tenant scratch prefix
   * (`bills/scratch/<companyId>/…`) — the router asserts this before
   * invoking the model. The SPA gets the key from
   * `bills.requestScratchUpload`.
   */
  sourceObjectKey: z.string().trim().min(1).max(512),
  /** MIME of the source — drives whether the model gets `image` or `file`. */
  contentType: z.enum(["application/pdf", "image/jpeg", "image/png", "image/webp"]),
});

/**
 * `bills.requestScratchUpload` — mint a presigned PUT URL for an uploaded
 * vendor bill BEFORE the bill row exists. Pairs with `bills.extract`
 * (operates against the scratch key) and `bills.create`
 * (`extractionSourceObjectKey` promotes the scratch object into a
 * permanent `bill_attachments` row).
 *
 * Distinct from `bills.attachments.requestUpload`: that one requires a
 * `billId` FK; this one mints a scratch key for the pre-bill flow.
 */
const attachmentMime = z.enum(["application/pdf", "image/jpeg", "image/png", "image/webp"]);
const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

export const requestScratchUploadInput = z.object({
  originalName: z.string().trim().min(1).max(255),
  contentType: attachmentMime,
  sizeBytes: z.number().int().min(1).max(MAX_ATTACHMENT_BYTES),
});

export const requestScratchUploadOutput = z.object({
  uploadUrl: z.string().url(),
  objectKey: z.string(),
  contentType: attachmentMime,
  maxBytes: z.number().int(),
});

/** Mirrors ExtractedBill in @flint/domain/ai/extractors/bills/types.ts. */
const extractedBillLine = z.object({
  description: z.string(),
  hsn: z.string(),
  qty: z.string(),
  unit: z.string(),
  rate: z.string(),
  taxRate: z.string(),
  discountRate: z.string(),
});

export const extractOutput = z.object({
  fields: z.object({
    vendor: z.object({
      name: z.string(),
      gstin: z.string().nullable(),
      pan: z.string().nullable(),
      address: z.string().nullable(),
    }),
    vendorDocNumber: z.string(),
    receivedOn: z.string(),
    dueOn: z.string().nullable(),
    lineItems: z.array(extractedBillLine),
    reportedSubtotal: z.string().nullable(),
    reportedTax: z.string().nullable(),
    reportedTotal: z.string().nullable(),
    notes: z.string().nullable(),
  }),
  /** Per-field 0..1 score keyed by dotted path. Missing key = no signal. */
  fieldConfidence: z.record(z.string(), z.number().min(0).max(1)),
  /** Weighted aggregate. Composer uses this for the "ready to issue" gate. */
  overallConfidence: z.number().min(0).max(1),
  /** OpenRouter model id used (e.g. 'anthropic/claude-sonnet-4'). */
  model: z.string(),
  /** Prompt revision — lets us correlate quality regressions to prompts. */
  promptVersion: z.string(),
  /** Approximate cost in paise. Soft signal, not invoice-grade. */
  costPaise: moneyPaise,
});

export const billStatus = z.enum([
  "draft",
  "review",
  "approval_pending",
  "ready_to_pay",
  "paid",
  "void",
]);

export const listInput = z.object({
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(50),
  status: billStatus.optional(),
  vendorId: uuid.optional(),
  dateFrom: isoDate.optional(),
  dateTo: isoDate.optional(),
  search: z.string().trim().min(1).max(80).optional(),
});

export const billLineInput = z.object({
  description: z.string().trim().min(1).max(500),
  hsn: z.string().trim().max(8).default(""),
  qty: z
    .string()
    .trim()
    .regex(/^\d+(\.\d{0,3})?$/),
  unit: z.string().trim().min(1).max(16),
  rate: z
    .string()
    .trim()
    .regex(/^\d+(\.\d{0,2})?$/),
  discountRate: z
    .string()
    .trim()
    .regex(/^\d+(\.\d{0,2})?$/)
    .default("0"),
  taxRate: z
    .string()
    .trim()
    .regex(/^\d+(\.\d{0,2})?$/)
    .default("18"),
  /**
   * Per-unit rate in the bill's foreign currency (decimal, e.g. "100.00" USD).
   * Required on every line of a foreign bill (ADR-0037); omit on INR bills. The
   * INR `rate` is derived from this server-side and ignored when present.
   */
  rateForeign: z
    .string()
    .trim()
    .regex(/^\d+(\.\d{0,2})?$/)
    .nullable()
    .optional(),
});

/**
 * Foreign-currency booking snapshot (ADR-0037, Phase 6a). Present on a non-INR
 * bill. INR remains the ledger currency — the server derives INR line amounts
 * from each line's `rateForeign` via `fxRateToInrPaise`.
 */
export const billFxInput = z.object({
  /** ISO-4217 3-letter code, e.g. "USD". */
  currencyCode: z
    .string()
    .trim()
    .regex(/^[A-Z]{3}$/),
  /** INR paise per 1 foreign MAJOR unit (positive integer string). */
  fxRateToInrPaise: moneyPaise,
  /** Rate date; defaults to the bill's receivedOn. */
  fxRateDate: isoDate.nullable().optional(),
});

export const billLineDetail = z.object({
  id: uuid,
  position: z.number().int().positive(),
  description: z.string(),
  hsn: z.string(),
  qtyMilli: z.number().int().nonnegative(),
  unit: z.string(),
  ratePaise: moneyPaise,
  discountBps: z.number().int().nonnegative(),
  taxRateBps: z.number().int().nonnegative(),
  grossPaise: moneyPaise,
  discountPaise: moneyPaise,
  taxablePaise: moneyPaise,
  taxPaise: moneyPaise,
  lineTotalPaise: moneyPaise,
  /** Per-line foreign amounts (minor units) when the bill is non-INR; null otherwise. */
  rateForeignMinor: moneyPaise.nullable(),
  grossForeignMinor: moneyPaise.nullable(),
  discountForeignMinor: moneyPaise.nullable(),
  taxableForeignMinor: moneyPaise.nullable(),
  taxForeignMinor: moneyPaise.nullable(),
  lineTotalForeignMinor: moneyPaise.nullable(),
});

export const createInput = z.object({
  vendorId: uuid,
  vendorDocNumber: z.string().trim().min(1, "Vendor invoice number is required").max(64),
  receivedOn: isoDate,
  dueOn: isoDate.nullable().optional(),
  subtotalPaise: moneyPaise,
  taxPaise: moneyPaise,
  totalPaise: moneyPaise,
  /**
   * Foreign-currency snapshot (ADR-0037). Omit for a domestic INR bill. When
   * set, the server derives the INR subtotal/tax/total from the lines'
   * `rateForeign` — the INR `subtotalPaise`/`taxPaise`/`totalPaise` above are
   * treated as a client estimate and recomputed.
   */
  fx: billFxInput.nullable().optional(),
  status: billStatus.default("draft"),
  threeWayMatch: z.enum(["matched", "mismatch", "na"]).default("na"),
  source: z.enum(["photo", "email_pdf", "manual"]).default("manual"),
  aiExtraction: z
    .object({
      model: z.string(),
      modelVersion: z.string(),
      sourceImageSha256: z.string().optional(),
      fieldConfidence: z.record(z.number()),
    })
    .nullable()
    .optional(),
  overallConfidence: z
    .string()
    .regex(/^\d+(\.\d{1,3})?$/)
    .nullable()
    .optional(),
  /** Free-form note printed on the bill (visible to vendor). Max 2000 chars. */
  vendorNote: z.string().trim().max(2000).nullable().optional(),
  /** Internal-only operator note. Max 2000 chars. */
  internalNote: z.string().trim().max(2000).nullable().optional(),
  /** Routed approver — display name or membership label. Max 255 chars. */
  approver: z.string().trim().max(255).nullable().optional(),
  lineItems: z.array(billLineInput).min(1).max(200).optional(),
  /**
   * Scratch object key from `bills.requestScratchUpload`. When present the
   * server promotes the scratch upload into a permanent `bill_attachments`
   * row inside the same transaction (atomic with the bill insert).
   * Operator-attached files (post-save additions) continue to use the
   * `bills.attachments.*` two-phase flow on the detail page.
   */
  extractionSourceObjectKey: z.string().trim().min(1).max(512).nullable().optional(),
  /**
   * Original filename + content-type + size of the scratch upload. Must be
   * present iff `extractionSourceObjectKey` is. Echoed back into the
   * attachment row so the bill detail page shows the operator's filename
   * rather than the random scratch suffix.
   */
  extractionAttachmentMeta: z
    .object({
      originalName: z.string().trim().min(1).max(255),
      contentType: attachmentMime,
      sizeBytes: z.number().int().min(1).max(MAX_ATTACHMENT_BYTES),
    })
    .nullable()
    .optional(),
});

export const createOutput = z.object({
  id: uuid,
});

export const getInput = z.object({
  id: uuid,
});

export const billDetail = z.object({
  id: uuid,
  vendorId: uuid,
  vendorName: z.string(),
  vendorGstin: z.string().nullable(),
  vendorCity: z.string().nullable(),
  vendorState: z.string().nullable(),
  vendorDocNumber: z.string().nullable(),
  receivedOn: isoDate,
  dueOn: isoDate.nullable(),
  subtotalPaise: moneyPaise,
  taxPaise: moneyPaise,
  totalPaise: moneyPaise,
  /** Foreign-currency snapshot (ADR-0037). `currencyCode` is "INR" for domestic bills. */
  currencyCode: z.string(),
  foreignAmountMinor: moneyPaise.nullable(),
  fxRateToInrPaise: moneyPaise.nullable(),
  fxRateDate: isoDate.nullable(),
  status: billStatus,
  threeWayMatch: z.enum(["matched", "mismatch", "na"]),
  source: z.enum(["photo", "email_pdf", "manual"]),
  vendorNote: z.string().nullable(),
  internalNote: z.string().nullable(),
  approver: z.string().nullable(),
  overallConfidence: z.string().nullable(),
  createdAt: z.string().datetime(),
  createdByActorId: z.string(),
  createdByActorType: z.string(),
  lineItems: z.array(billLineDetail),
});

export const getOutput = billDetail;

export const updateInput = z.object({
  id: uuid,
  vendorDocNumber: z.string().trim().min(1, "Vendor invoice number is required").max(64),
  receivedOn: isoDate,
  dueOn: isoDate.nullable().optional(),
  subtotalPaise: moneyPaise,
  taxPaise: moneyPaise,
  totalPaise: moneyPaise,
  threeWayMatch: z.enum(["matched", "mismatch", "na"]),
  source: z.enum(["photo", "email_pdf", "manual"]),
  vendorNote: z.string().trim().max(2000).nullable().optional(),
  internalNote: z.string().trim().max(2000).nullable().optional(),
  approver: z.string().trim().max(255).nullable().optional(),
  lineItems: z.array(billLineInput).min(1).max(200).optional(),
});

export const updateOutput = z.object({
  id: uuid,
});

export const transitionStatusInput = z.object({
  id: uuid,
  status: billStatus,
  /**
   * Resubmit token (ADR-0031). When a configured `vendor_bill` chain gated the
   * approve-for-payment step (transition into `ready_to_pay`), the operator
   * approves in the inbox and resends with the `pending_approvals.id` here.
   */
  approvalId: uuid.optional(),
});

export const transitionStatusOutput = z.object({
  id: uuid,
  /** `transitioned` = status changed; `awaiting_approval` = a chain is pending. */
  outcome: z.enum(["transitioned", "awaiting_approval"]),
  status: billStatus,
  /** Filed `pending_approvals.id` when `outcome='awaiting_approval'`, else null. */
  approvalId: uuid.nullable(),
});

/**
 * `bills.batchApprove` — Founder-only bulk approve of `approval_pending`
 * bills, gated by step-up OTP (`STEP_UP_ACTIONS.BILLS_BATCH_APPROVE`).
 *
 * Server processes each id in order, transitioning to `ready_to_pay`.
 * Per-id outcomes are returned — one bad row doesn't roll back the
 * others. The SPA shows "Approved N of M" and keeps failed rows
 * selected for retry / inspection.
 */
export const batchApproveInput = z.object({
  /** 1-50 bills per call (server caps to keep the audit boundary tight). */
  billIds: z.array(uuid).min(1).max(50),
});

export const batchApproveOutput = z.object({
  succeeded: z.array(uuid),
  failed: z.array(
    z.object({
      id: uuid,
      reason: z.string(),
    }),
  ),
  rows: z.array(
    z.object({
      id: uuid,
      status: billStatus.nullable(),
      failureReason: z.string().nullable(),
    }),
  ),
});

/**
 * `deleteDraft` — abandon a draft bill. The domain refuses non-drafts;
 * past-draft rows must go through `transitionStatus` to `void` instead.
 */
export const deleteDraftInput = z.object({
  id: uuid,
});

export const deleteDraftOutput = z.object({
  id: uuid,
  vendorId: uuid,
  totalPaise: moneyPaise,
});

export const listOutput = paged(
  z.object({
    id: uuid,
    vendorId: uuid,
    vendorName: z.string(),
    vendorGstin: z.string().nullable(),
    vendorCity: z.string().nullable(),
    vendorState: z.string().nullable(),
    vendorDocNumber: z.string().nullable(),
    receivedOn: isoDate,
    dueOn: isoDate.nullable(),
    totalPaise: moneyPaise,
    status: billStatus,
    threeWayMatch: z.enum(["matched", "mismatch", "na"]),
    source: z.enum(["photo", "email_pdf", "manual"]),
    createdAt: z.string().datetime(),
  }),
).extend({
  counts: z.object({
    all: z.number().int().nonnegative(),
    draft: z.number().int().nonnegative(),
    review: z.number().int().nonnegative(),
    approval_pending: z.number().int().nonnegative(),
    ready_to_pay: z.number().int().nonnegative(),
    paid: z.number().int().nonnegative(),
    void: z.number().int().nonnegative(),
  }),
  kpis: z.object({
    outstandingPaise: moneyPaise,
    outstandingBillCount: z.number().int().nonnegative(),
    outstandingVendorCount: z.number().int().nonnegative(),
    awaitingApprovalPaise: moneyPaise,
    approvalReadyCount: z.number().int().nonnegative(),
    heldForProofCount: z.number().int().nonnegative(),
    paidThisMonthPaise: moneyPaise,
    paidThisMonthCount: z.number().int().nonnegative(),
    dueThisWeekPaise: moneyPaise,
    dueThisWeekCount: z.number().int().nonnegative(),
    recurringVendorCount: z.number().int().nonnegative(),
    recurringMonthlyPaise: moneyPaise,
    averageBillPaise: moneyPaise,
    topVendorName: z.string().nullable(),
    topVendorBillCount: z.number().int().nonnegative(),
  }),
});

// ── export (CSV / Excel / PDF) ────────────────────────────────────
/**
 * Filter for the export surfaces — the SAME server-supported filters the
 * list query carries (status, vendor, date window, free-text). No cursor:
 * the export spans the whole filtered set (capped server-side). Client-only
 * narrowing (multi-status tabs, amount buckets) is not part of the server
 * contract, so an export reflects the server-filtered view.
 */
export const exportFilter = z.object({
  status: billStatus.optional(),
  vendorId: uuid.optional(),
  dateFrom: isoDate.optional(),
  dateTo: isoDate.optional(),
  search: z.string().trim().min(1).max(80).optional(),
});

/** One flattened bill row for the export. Money stays integer paise. */
export const exportRow = z.object({
  vendorName: z.string(),
  vendorGstin: z.string().nullable(),
  vendorCity: z.string().nullable(),
  vendorState: z.string().nullable(),
  vendorDocNumber: z.string().nullable(),
  receivedOn: isoDate,
  dueOn: isoDate.nullable(),
  totalPaise: moneyPaise,
  status: billStatus,
  threeWayMatch: z.enum(["matched", "mismatch", "na"]),
  source: z.enum(["photo", "email_pdf", "manual"]),
  createdAt: z.string().datetime(),
});

export const exportRowsInput = exportFilter;
export const exportRowsOutput = z.object({
  rows: z.array(exportRow),
  generatedAt: z.string().datetime(),
});

export const exportPdfInput = exportFilter;
export const exportPdfOutput = z.object({
  fileName: z.string(),
  pdfBase64: z.string(),
});

export type ExportFilter = z.infer<typeof exportFilter>;
export type ExportRow = z.infer<typeof exportRow>;
export type ExportRowsInput = z.infer<typeof exportRowsInput>;
export type ExportRowsOutput = z.infer<typeof exportRowsOutput>;
export type ExportPdfInput = z.infer<typeof exportPdfInput>;
export type ExportPdfOutput = z.infer<typeof exportPdfOutput>;
